/* oxlint-disable eslint/max-lines */
import { showSignUpModalAtom } from '@/atoms/chatAtoms';
import { clinicianTrialModalOpenAtom } from '@/atoms/paywallAtoms';
import { Button } from '@heidi/design-system/button';
import { TierPracticeFilled, TierProFilled } from '@heidi/design-system/icons';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { useOpenEvidenceUpgradeModal } from '@/features/growth/deprecate-evidence-plus/useOpenEvidenceUpgradeModal';
import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import useReverseTrial from '@/hooks/useReverseTrial';
import { useTrackSignUpWallViewed } from '@/hooks/useTrackSignUpWallViewed';
import { cn } from '@/lib/cn';
import { EvidenceSignUpWall } from '@/models/analytics/features/evidence';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useSetAtom } from 'jotai';
import { Lock, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

export interface SourceControlPopoverV2BannersProps {
  /** Called after any banner action so the parent can close the popover/drawer */
  onClose?: () => void;
  className?: string;
  variant?: 'popover' | 'drawer';
  isTeamEnforced?: boolean;
}

export const SourceControlPopoverV2Banners = ({
  onClose,
  className,
  variant = 'popover',
  isTeamEnforced = false,
}: SourceControlPopoverV2BannersProps) => {
  const intl = useIntl();
  const trackSignUpWallViewed = useTrackSignUpWallViewed();
  const {
    isInFreeTrial: isInEvidenceTrial,
    trialDaysRemaining,
    freeTrialAvailable: evidenceFreeTrialAvailable,
  } = useEvidenceBillingInfo();
  const { data: billingInfo } = useQueryUserBillingInfo();
  const isNonEvidenceTier = [
    SubscriptionTier.FREE,
    SubscriptionTier.PRO,
    SubscriptionTier.PRO_PLUS,
  ].includes(billingInfo?.new_product_tier ?? SubscriptionTier.FREE);
  const isScribePlusUser = billingInfo?.new_product_tier === SubscriptionTier.PRO;
  const isScribeTeamUser = billingInfo?.new_product_tier === SubscriptionTier.PRO_PLUS;
  const isEvidenceTrialUser = isInEvidenceTrial && billingInfo?.new_product_tier === SubscriptionTier.EVIDENCE;
  const {
    isClinicianTrialUser,
    clinicianTrialDaysLeft,
    isClinicianPracticeTrialUser,
    clinicianTrialAvailable,
  } = useReverseTrial();
  const { isAuthenticated } = useIsAuthenticated();

  const setClinicianTrialModalOpen = useSetAtom(clinicianTrialModalOpenAtom);
  const { openEvidenceUpgradeModal } = useOpenEvidenceUpgradeModal();
  const setShowSignUpModal = useSetAtom(showSignUpModalAtom);

  const handleUpgradeClick = useCallback(() => {
    openEvidenceUpgradeModal({
      isScribePlusUser,
      isClinicianTrialUser,
      isPracticeUpgradeTarget: isScribeTeamUser || isClinicianPracticeTrialUser,
    });
    onClose?.();
  }, [
    isScribePlusUser,
    isClinicianTrialUser,
    isClinicianPracticeTrialUser,
    isScribeTeamUser,
    openEvidenceUpgradeModal,
    onClose,
  ]);

  const handleStartTrialClick = useCallback(() => {
    setClinicianTrialModalOpen(true);
    onClose?.();
  }, [setClinicianTrialModalOpen, onClose]);

  const handleSignUpClick = useCallback(() => {
    trackSignUpWallViewed(EvidenceSignUpWall.SourceSelectionWall);
    setShowSignUpModal(true);
    onClose?.();
  }, [trackSignUpWallViewed, setShowSignUpModal, onClose]);

  const isDrawer = variant === 'drawer';
  const iconSizeClass = isDrawer ? 'size-3' : 'size-2.5';
  const textSize = isDrawer ? 'text-sm' : 'text-xs';
  const buttonSize = isDrawer ? ('sm' as const) : ('xs' as const);
  const bannerClassName = cn(
    'flex w-full items-center justify-center gap-3 bg-tier-pro-muted transition-colors hover:bg-tier-pro-muted/80',
    isDrawer ? 'p-4 active:bg-tier-pro-muted/70' : 'cursor-pointer px-4 py-3',
    !isDrawer && className
  );
  const practiceBannerClassName = cn(
    'flex w-full items-center justify-center gap-3 bg-tier-practice-muted transition-colors hover:bg-tier-practice-muted/80',
    isDrawer ? 'p-4 active:bg-tier-practice-muted/70' : 'cursor-pointer px-4 py-3',
    !isDrawer && className
  );
  const startTrialClassName = cn(
    'flex w-full items-center gap-2 bg-tier-pro-muted transition-colors hover:bg-tier-pro-muted/80',
    isDrawer ? 'p-4 active:bg-tier-pro-muted/70' : 'cursor-pointer px-4 py-3',
    !isDrawer && className
  );
  const showStartTrialCta = isNonEvidenceTier && evidenceFreeTrialAvailable && clinicianTrialAvailable && isAuthenticated;
  const showUpgradeBanner = isNonEvidenceTier && !(evidenceFreeTrialAvailable && clinicianTrialAvailable);
  const upgradeText = intl.formatMessage({ id: '0h/lPMeYkq', defaultMessage: 'Upgrade' });

  return (
    <>
      {/* Clinician trial days remaining — user already activated clinician trial */}
      {isClinicianTrialUser && (
        <button
          type="button"
          onClick={handleUpgradeClick}
          className={bannerClassName}
          aria-label={upgradeText}
        >
          <div className="flex items-center gap-2">
            <TierProFilled className={cn(iconSizeClass, 'text-tier-pro')} />
            <span className={cn(textSize, 'text-tier-pro-text')}>
              <FormattedMessage 
                id="qGcwMWToIe"
                defaultMessage="{count, plural, one {# day} other {# days}} left on your <b>Clinician Trial</b>"
                values={{
                  count: clinicianTrialDaysLeft,
                  b: (chunks) => <span className="font-bold">{chunks}</span>,
                }}
              />
            </span>
          </div>
          <Button variant="pro" size={buttonSize} asChild>
            <span>{upgradeText}</span>
          </Button>
        </button>
      )}

      {/* Practice trial days remaining — user already activated practice trial */}
      {isClinicianPracticeTrialUser && (
        <button
          type="button"
          onClick={handleUpgradeClick}
          className={practiceBannerClassName}
          aria-label={upgradeText}
        >
          <div className="flex items-center gap-2">
            <TierPracticeFilled className={cn(iconSizeClass, 'text-tier-practice')} />
            <span className={cn(textSize, 'text-tier-practice-text')}>
              <FormattedMessage 
                id="pwbHU/o+xg"
                defaultMessage="{count, plural, one {# day} other {# days}} left on your <b>Practice Trial</b>"
                values={{
                  count: clinicianTrialDaysLeft,
                  b: (chunks) => <span className="font-bold">{chunks}</span>,
                }}
              />
            </span>
          </div>
          <Button variant="pro_plus" size={buttonSize} asChild>
            <span>{upgradeText}</span>
          </Button>
        </button>
      )}

      {/* Evidence Pro trial banner — hidden when user is on clinician or practice trial */}
      {isEvidenceTrialUser && (
        <button
          type="button"
          onClick={handleUpgradeClick}
          className={bannerClassName}
          aria-label={upgradeText}
        >
          <div className="flex items-center gap-2">
            <TierProFilled className={cn(iconSizeClass, 'text-tier-pro')} />
            <span className={cn(textSize, 'text-tier-pro-text')}>
              <FormattedMessage 
                id="9wjLjBNlFY"
                defaultMessage="{count, plural, one {# day} other {# days}} left on your <b>Evidence Plus Trial</b>"
                values={{
                  count: trialDaysRemaining,
                  b: (chunks) => <span className="font-bold">{chunks}</span>,
                }}
              />
            </span>
          </div>
          <Button variant="pro" size={buttonSize} asChild>
            <span>{upgradeText}</span>
          </Button>
        </button>
      )}

      {showUpgradeBanner && (
        <button
          type="button"
          onClick={handleUpgradeClick}
          className={bannerClassName}
          aria-label={upgradeText}
        >
          <span className={cn(textSize, 'font-medium text-text-primary')}>
            <FormattedMessage
              id="bh5MZbE/dz"
              defaultMessage="Continue using curated clinical sources"
            />
          </span>
          <Button variant="pro" size={buttonSize} asChild>
            <span>
              <TierProFilled />
              {upgradeText}
            </span>
          </Button>
        </button>
      )}

      {showStartTrialCta && (
        <button
          type="button"
          onClick={handleStartTrialClick}
          className={startTrialClassName}
          aria-label={(isScribePlusUser
              ? intl.formatMessage({ defaultMessage: 'Try Clinician Plan', id: 'IkOH8MNqKu' })
              : intl.formatMessage({ defaultMessage: 'Try Evidence Plus', id: 'udTGEo3iXV' })
          )}
        >
          <TierProFilled className={cn(iconSizeClass, 'text-tier-pro')} />
          <span className={cn(textSize, 'font-bold text-tier-pro-text')}>
            {(isScribePlusUser
                ? intl.formatMessage({ defaultMessage: 'Try Clinician Plan', id: 'IkOH8MNqKu' })
                : intl.formatMessage({ defaultMessage: 'Try Evidence Plus', id: 'udTGEo3iXV' })
            )}
          </span>
          <span className={cn(textSize, 'text-text-secondary')}>
            {(isScribePlusUser
              ? intl.formatMessage({ defaultMessage: 'Experience 14 days of the Clinician plan', id: 'DZhcu9wZpz' })
              : intl.formatMessage({ defaultMessage: 'Experience 14 days of source control', id: 'D8cWB+JDyB' })
            )}
          </span>
        </button>
      )}

      {!isAuthenticated && (
        <div className={cn('flex flex-col gap-4 rounded-b-lg bg-surface-accent p-4', className)}>
          <div className="flex flex-col gap-1">
            <TypographyP2 className="font-medium text-text-primary">
              {intl.formatMessage({ defaultMessage: 'Unlock source control', id: 'RS7115UKon' })}
            </TypographyP2>
            <TypographyCaption className="text-text-secondary">
              {intl.formatMessage({ defaultMessage: 'Make every decision backed by evidence you trust.', id: 'pEdFjkjEcg' })}
            </TypographyCaption>
          </div>
          <Button
            type="button"
            variant="default"
            size="md"
            onClick={handleSignUpClick}
            className="w-full"
          >
            {intl.formatMessage({ defaultMessage: 'Sign up for free', id: 'Rtxm5TiAN3' })}
          </Button>
        </div>
      )}
    </>
  );
};

const ENFORCEMENT_BANNER_STORAGE_KEY = 'heidi:enforcement-banner-dismissed';
const ENFORCEMENT_BANNER_SESSION_KEY = 'heidi:enforcement-banner-seen';

export const TeamEnforcementBanner = ({ isTeamEnforced }: { isTeamEnforced: boolean }) => {
  const intl = useIntl();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const permanentlyDismissed = localStorage.getItem(ENFORCEMENT_BANNER_STORAGE_KEY) === 'true';
    if (permanentlyDismissed) return;

    const seenThisSession = sessionStorage.getItem(ENFORCEMENT_BANNER_SESSION_KEY) === 'true';
    if (seenThisSession) {
      localStorage.setItem(ENFORCEMENT_BANNER_STORAGE_KEY, 'true');
      return;
    }

    sessionStorage.setItem(ENFORCEMENT_BANNER_SESSION_KEY, 'true');
    setVisible(true);
  }, []);

  if (!isTeamEnforced || !visible) return null;

  const handleDismiss = () => {
    localStorage.setItem(ENFORCEMENT_BANNER_STORAGE_KEY, 'true');
    setVisible(false);
  };

  return (
    <div className="flex w-full items-center gap-2 rounded-t-lg bg-surface-accent px-4 py-2.5">
      <Lock size={12} className="shrink-0 text-text-tertiary" />
      <span className="flex-1 text-xs text-text-secondary">
        {intl.formatMessage({ id: 'kf7Dyqpkkh', defaultMessage: 'Some sources are required by your organisation' })}
      </span>
      <button
        type="button"
        onClick={handleDismiss}
        className="flex shrink-0 items-center justify-center rounded-sm p-0.5 text-text-tertiary hover:text-text-secondary"
        aria-label="Dismiss"
      >
        <X size={12} />
      </button>
    </div>
  );
};
